import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FaTimes } from "react-icons/fa";
import API from "../api/api";
import { successAlert } from "../utils/alert";

const INITIAL_FORM = {
  name: "",
  father_name: "",
  age: "",
  organisation: "",
  mobile: "",
  email: "",
  district: "",
};

const VolunteerRegistrationModal = ({ event, onClose, onSubmitted }) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);

  useEffect(() => {
    if (!event) return;
    setSubmitting(false);
    setError("");
    setForm(INITIAL_FORM);
  }, [event]);

  if (!event) return null;

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!event?.id) return;

    setSubmitting(true);
    setError("");
    try {
      const payload = {
        ...form,
        organisation: String(form.organisation || "").trim() || "Individual",
      };
      await API.registerVolunteerForEvent(event.id, payload);
      onClose?.();
      onSubmitted?.(event);
      successAlert(
        "Registration submitted",
        "Volunteer registration submitted successfully. Admin approval is pending."
      );
    } catch (err) {
      setError(err?.message || "Unable to submit volunteer registration.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        <div className="relative max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
          <div className="p-6">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold text-slate-900">
                Volunteer / Escort Registration
              </h3>
              <button onClick={onClose} className="text-slate-500 hover:text-slate-800 transition-colors">
                <FaTimes size={22} />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Event: <strong>{event.title}</strong>
            </p>

            {error ? (
              <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                {error}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <input
                type="text"
                name="name"
                value={form.name}
                onChange={handleFieldChange}
                placeholder="Name"
                className="border rounded px-3 py-2"
                required
              />
              <input
                type="text"
                name="father_name"
                value={form.father_name}
                onChange={handleFieldChange}
                placeholder="Father Name"
                className="border rounded px-3 py-2"
                required
              />
              <input
                type="number"
                min="10"
                max="100"
                name="age"
                value={form.age}
                onChange={handleFieldChange}
                placeholder="Age"
                className="border rounded px-3 py-2"
                required
              />
              <input
                type="text"
                name="organisation"
                value={form.organisation}
                onChange={handleFieldChange}
                placeholder="Organisation (Individual if none)"
                className="border rounded px-3 py-2"
                required
              />
              <input
                type="tel"
                name="mobile"
                value={form.mobile}
                onChange={handleFieldChange}
                placeholder="Mobile"
                className="border rounded px-3 py-2"
                required
              />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleFieldChange}
                placeholder="Email (for updates)"
                className="border rounded px-3 py-2"
              />
              <input
                type="text"
                name="district"
                value={form.district}
                onChange={handleFieldChange}
                placeholder="District"
                className="border rounded px-3 py-2"
                required
              />

              <div className="sm:col-span-2 flex gap-3 mt-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className={`font-semibold text-white rounded-lg px-5 py-2 transition-all ${
                    submitting
                      ? "bg-slate-400 cursor-not-allowed"
                      : "bg-emerald-600 hover:bg-emerald-700"
                  }`}
                >
                  {submitting ? "Submitting..." : "Submit for Approval"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="font-semibold text-slate-700 bg-slate-200 rounded-lg px-5 py-2 hover:bg-slate-300 transition-all"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in-up {
          0% { opacity: 0; transform: translateY(20px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

VolunteerRegistrationModal.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
  }),
  onClose: PropTypes.func,
  onSubmitted: PropTypes.func,
};

VolunteerRegistrationModal.defaultProps = {
  event: null,
  onClose: undefined,
  onSubmitted: undefined,
};

export default VolunteerRegistrationModal;
